import { Decimal } from 'decimal.js';
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_EVEN });
export { Decimal };
const MINOR_UNITS = {
    JPY: 0, KRW: 0, VND: 0, CLP: 0, ISK: 0, HUF: 2,
    BHD: 3, KWD: 3, OMR: 3, JOD: 3, IQD: 3, TND: 3, LYD: 3,
};
export const minorUnits = (ccy) => MINOR_UNITS[ccy.toUpperCase()] ?? 2;
export const assertCents = (n, what = 'amount') => {
    if (typeof n !== 'number' || !Number.isSafeInteger(n))
        throw new TypeError(`${what} must be an integer number of minor units, got ${String(n)}`);
    return n;
};
export const addCents = (...xs) => xs.reduce((a, b) => a + assertCents(b), 0);
export const subCents = (a, b) => assertCents(a) - assertCents(b);
export const negCents = (a) => -assertCents(a);
export const absCents = (a) => Math.abs(assertCents(a));
export const sumCents = (xs) => addCents(...xs);
export const cmpCents = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
/** Multiply cents by a decimal factor, rounding half-even. */
export const mulCents = (a, factor) => new Decimal(assertCents(a)).mul(factor).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
/** Allocate `total` cents across `weights` so the parts sum exactly to `total` (largest-remainder). */
export const allocateCents = (total, weights) => {
    assertCents(total);
    const w = weights.map((x) => Math.max(0, x));
    const sum = w.reduce((a, b) => a + b, 0);
    if (w.length === 0)
        return [];
    if (sum === 0)
        return allocateCents(total, w.map(() => 1));
    const raw = w.map((x) => (total * x) / sum);
    const parts = raw.map((x) => Math.trunc(x));
    let remainder = total - parts.reduce((a, b) => a + b, 0);
    const order = raw.map((x, i) => ({ i, frac: Math.abs(x - Math.trunc(x)) })).sort((a, b) => b.frac - a.frac);
    const step = remainder > 0 ? 1 : -1;
    for (let k = 0; remainder !== 0; k = (k + 1) % order.length) {
        const idx = order[k].i;
        parts[idx] = parts[idx] + step;
        remainder -= step;
    }
    return parts;
};
/** "12.34" (major units, any sign/commas) → cents. Throws on garbage. */
export const parseMoney = (s, ccy = 'AUD') => {
    const mu = minorUnits(ccy);
    if (typeof s === 'number')
        return new Decimal(s).mul(10 ** mu).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
    let t = s.trim().replace(/[,\s]/g, '').replace(/^[A-Z]{3}\s?/i, '').replace(/[$£€¥]/g, '');
    let neg = false;
    if (/^\(.*\)$/.test(t)) {
        neg = true;
        t = t.slice(1, -1);
    }
    if (t.endsWith('-')) {
        neg = true;
        t = t.slice(0, -1);
    }
    if (t.startsWith('-')) {
        neg = !neg;
        t = t.slice(1);
    }
    if (t.startsWith('+'))
        t = t.slice(1);
    if (t === '' || !/^\d*(\.\d*)?$/.test(t) || t === '.')
        throw new TypeError(`Cannot parse money: ${JSON.stringify(s)}`);
    const d = new Decimal(t).mul(10 ** mu).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN);
    return (neg ? d.neg() : d).toNumber();
};
export const centsToDecimal = (c, ccy = 'AUD') => new Decimal(assertCents(c)).div(10 ** minorUnits(ccy));
export const decimalToCents = (d, ccy = 'AUD') => new Decimal(d).mul(10 ** minorUnits(ccy)).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
/** Format cents for display using Intl. `{ sign: 'auto' | 'always' | 'never' }`, `compact` for 12.3k. */
export const formatMoney = (c, ccy = 'AUD', locale = 'en-AU', opts = {}) => {
    const mu = minorUnits(ccy);
    const value = assertCents(c) / 10 ** mu;
    const nf = new Intl.NumberFormat(locale, {
        style: opts.hideSymbol ? 'decimal' : 'currency',
        currency: ccy,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: opts.compact ? 0 : mu,
        maximumFractionDigits: opts.compact ? 1 : mu,
        notation: opts.compact ? 'compact' : 'standard',
        signDisplay: opts.sign === 'always' ? 'exceptZero' : opts.sign === 'never' ? 'never' : 'auto',
    });
    return nf.format(value);
};
export const dec = (v) => new Decimal(v);
export const decStr = (v, dp = 6) => new Decimal(v).toDecimalPlaces(dp, Decimal.ROUND_HALF_EVEN).toString();
export const isZeroDec = (v) => new Decimal(v).isZero();
//# sourceMappingURL=money.js.map